import debounce from 'lodash.debounce';
import { useEffect, useMemo } from 'react';

import { useLatest } from './use-latest';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Func = (...args: any[]) => any;

type DebouncedFunc<T extends Func> = {
  (...args: Parameters<T>): ReturnType<T> | undefined;
  cancel(): void;
  flush(): ReturnType<T> | undefined;
};

export const useDebouncedFunc = <T extends Func>(
  func: T,
  wait: number,
  settings: {
    leading?: boolean;
    maxWait?: number;
    trailing?: boolean;
  } = {},
): DebouncedFunc<T> => {
  const funcRef = useLatest(func);

  const debouncedFunc = useMemo(() => {
    return debounce(
      (...args: Parameters<T>): ReturnType<T> => funcRef.current(...args),
      wait,
      settings,
    );
  }, []);

  useEffect(() => {
    return () => debouncedFunc.cancel();
  }, []);

  return debouncedFunc;
};
